import * as cloneDeep from 'lodash/cloneDeep';
import * as get from 'lodash/get';

import { BASE_CHART_CONFIG } from '../../configs/common/base-chart.config';
import { TooltipDispatcher } from '../../dispatcher/tooltip.dispatcher';
import { IHighChartsAttributesModel } from '../../models/high-charts-attributes.interface';
import { IChartWorker } from './chart.worker.interface';

declare const Highcharts;

/**
 * BaseChartWorker: a class containing common properties and methods utilized by all chart worker objects.
 * @param {any} config JSON object representing the chart configuration.
 * @param {IHighChartsAttributesModel} highChartsAttributes an object containing properties used to augment the chart configuration.
 */
export abstract class BaseChartWorker implements IChartWorker {
	private originalWidth: number;
	private originalPtWidth: number;

	public chart: any;
	public config: any;
	public highChartsAttributes: IHighChartsAttributesModel;
	public transformedData: any;

	constructor(config: any, highChartsAttributes: IHighChartsAttributesModel) {
		this.highChartsAttributes = highChartsAttributes;
		const chartSpecificConfig = cloneDeep(config);
		this.config = { ...BASE_CHART_CONFIG, ...chartSpecificConfig };
		this.configureHighChartAttributes();
		this.initChart();
	}

	private initChart(): void {
		this.originalWidth = this.config.chart.width ? this.config.chart.width : this.highChartsAttributes.container.nativeElement.offsetWidth;
		this.originalPtWidth =
			this.config.plotOptions && this.config.plotOptions.column && this.config.plotOptions.column.pointWidth ? this.config.plotOptions.column.pointWidth : null;

		// set tooltip
		if (!this.config.tooltip?.formatter && this.highChartsAttributes.tooltipType) {
			this.config.tooltip.formatter = TooltipDispatcher.getTooltipFormatter(this.highChartsAttributes.tooltipType);
		}

		// set base pdf specific attributes
		if (this.highChartsAttributes.isPdf) {
			this.config.tooltip.enabled = false;
			this.config.responsive = null;
		}
	}

	private render(): void {
		// static reference to allow for chart data updates rather than complete re-render of the chart
		this.chart = Highcharts.chart(this.highChartsAttributes.container.nativeElement, this.config);
	}

	/**
	 * initializeChart: initializes the chart upon the DOM and event listeners tied to resizing of the chart.
	 */
	public initializeChart(): void {
		if (this.config.series) {
			setTimeout(() => {
				this.updateConfiguration();
				this.render();
			}, 100);
			window.addEventListener(
				'resize',
				this.debounce(() => {
					this.updateConfiguration();
					this.render();
				})
			);
		}
	}

	/**
	 * updateConfiguration: called when the window is resized -- used to update the chart configuration.
	 */
	private updateConfiguration(): void {
		const width = this.highChartsAttributes.container.nativeElement.offsetWidth;
		this.config.chart.width = this.config.chart.width && width;

		// If we have a ptWidth, we'll resize it as the window shrinks or expands. This will stop the columns from
		// rendering on top of on another.
		if (this.originalPtWidth) {
			const ptWidth = this.originalPtWidth * (parseFloat(width) / this.originalWidth);
			this.config.plotOptions.column.pointWidth = ptWidth < this.originalPtWidth ? ptWidth : this.originalPtWidth;
			const heightOffset = get(this.highChartsAttributes, 'container.nativeElement.offsetHeight', 0);
			this.config.chart.height = heightOffset > 0
				? this.highChartsAttributes.container.nativeElement.offsetHeight
				: this.config.chart.height;
		}
	}

	/**
	 * debounce: simple debounce method used to track the end of the window resize event. This stops the constant
	 * resizing/rendering of the chart as the window is resized.
	 */
	private debounce(func): any {
		let timer;
		// eslint-disable-next-line
		return function (event) {
			if (timer) { clearTimeout(timer); }
			timer = setTimeout(func, 500, event);
		};
	}

	private configureHighChartAttributes(): void {
		if (this.highChartsAttributes.axisType === 'xAxis') {
			this.config['yAxis'] = null;
			this.config['xAxis']['min'] = this.highChartsAttributes.min ? this.highChartsAttributes.min : 0;
			this.config['xAxis']['max'] = this.highChartsAttributes.max ? this.highChartsAttributes.max : this.config['xAxis']['max'];
			this.config['xAxis']['tickInterval'] = this.highChartsAttributes.tickInterval ? this.highChartsAttributes.tickInterval : this.config['xAxis']['tickInterval'];
		}

		if (this.highChartsAttributes.axisType === 'yAxis') {
			this.config['xAxis'] = null;
			this.config['yAxis']['min'] = this.highChartsAttributes.min ? this.highChartsAttributes.min : 0;
			this.config['yAxis']['max'] = this.highChartsAttributes.max ? this.highChartsAttributes.max : this.config['yAxis']['max'];
			this.config['yAxis']['tickInterval'] = this.highChartsAttributes.tickInterval ? this.highChartsAttributes.tickInterval : this.config['yAxis']['tickInterval'];
		}

		this.config.chart['renderTo'] = this.highChartsAttributes.container.nativeElement;
		this.config.title['text'] = this.highChartsAttributes.showTitle ? this.highChartsAttributes.title : this.config.title['text'];
		this.config.chart['height'] = this.highChartsAttributes.height ? this.highChartsAttributes.height : this.config.chart['height'];
		this.config.chart['width'] = this.highChartsAttributes.width ? this.highChartsAttributes.width : this.config.chart['width'];
		this.config['colors'] = this.highChartsAttributes.colors ? this.highChartsAttributes.colors : this.config['colors'];

		if (this.highChartsAttributes.customAttributes) {
			this.config = { ...this.config, ...this.highChartsAttributes.customAttributes };
		}
	}

	public transformData(): void {
		// transformData: implemented in classes that extend this BaseChartWorker
	}

	public updateData(data: Array<any>): void {
		// updateData: implement in classes that extend this BaseChartWorker
	}
}
